'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Search, FileText, Scissors, Crown, Plane, Smile, ArrowLeft } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { useLocalizedNumbers } from '@/hooks/useLocalizedNumbers';
import { cn } from '@/lib/utils';

gsap.registerPlugin(ScrollTrigger);

export default function ProcessStepsSection() {
    const { t } = useTranslation('dental_implants');
    const { isRTL } = useRTL();
    const { localizeText } = useLocalizedNumbers();
    const sectionRef = useRef<HTMLElement>(null);
    const stepsRef = useRef<HTMLDivElement>(null);

    const steps = [
        {
            number: 1,
            icon: <Search className="w-8 h-8 text-gold" />,
            title: t('process.steps.consultation.title'),
            description: localizeText(t('process.steps.consultation.description')),
            duration: localizeText(t('process.steps.consultation.duration'))
        },
        {
            number: 2,
            icon: <FileText className="w-8 h-8 text-gold" />,
            title: t('process.steps.planning.title'),
            description: localizeText(t('process.steps.planning.description')),
            duration: localizeText(t('process.steps.planning.duration'))
        },
        {
            number: 3,
            icon: <Scissors className="w-8 h-8 text-gold" />,
            title: t('process.steps.surgery.title'),
            description: localizeText(t('process.steps.surgery.description')),
            duration: localizeText(t('process.steps.surgery.duration'))
        },
        {
            number: 4,
            icon: <Crown className="w-8 h-8 text-gold" />,
            title: t('process.steps.healing.title'),
            description: localizeText(t('process.steps.healing.description')),
            duration: localizeText(t('process.steps.healing.duration'))
        }
    ];

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Staggered animation for steps
            if (stepsRef.current?.children) {
                Array.from(stepsRef.current.children).forEach((step, index) => {
                    gsap.fromTo(step,
                        { opacity: 0, y: 60 },
                        {
                            opacity: 1,
                            y: 0,
                            duration: 0.8,
                            delay: index * 0.2,
                            ease: "power3.out",
                            scrollTrigger: {
                                trigger: step,
                                start: "top 85%",
                                toggleActions: "play none none reverse"
                            }
                        }
                    );
                });
            }
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            id="process"
            ref={sectionRef}
            className="py-20 bg-white dark:bg-slate-800"
        >
            <div className="container">
                <div className="text-center mb-16">
                    <h2 className={cn(
                        "text-4xl md:text-5xl font-bold text-navy dark:text-white mb-6 font-serif",
                        isRTL && "font-arabic"
                    )}>
                        {t('process.title_part1')}{' '}
                        <span className="text-gold">{t('process.title_part2')}</span>
                    </h2>
                </div>

                {/* Steps Timeline */}
                <div ref={stepsRef} className="relative max-w-4xl mx-auto">
                    {steps.map((step, index) => (
                        <div key={index} className="relative mb-12 last:mb-0">
                            {/* Timeline line */}
                            {index < steps.length - 1 && (
                                <div className={cn(
                                    "absolute top-20 w-px h-16 bg-slate-200 dark:bg-slate-700",
                                    isRTL ? "right-8" : "left-8"
                                )}></div>
                            )}

                            <div className={cn(
                                "flex items-start",
                                isRTL ? "space-x-reverse space-x-6" : "space-x-6"
                            )}>
                                {/* Step number and icon */}
                                <div className="shrink-0">
                                    <div className={cn(
                                        "w-16 h-16 bg-gold rounded-full flex items-center justify-center text-navy font-bold text-xl shadow-lg",
                                        isRTL && "font-arabic"
                                    )}>
                                        {localizeText(step.number.toString())}
                                    </div>
                                </div>

                                {/* Content card */}
                                <div className="flex-1 bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg dark:border dark:border-slate-700 hover:shadow-xl transition-all duration-300">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className={cn(
                                            "flex items-center",
                                            isRTL ? "space-x-reverse space-x-3" : "space-x-3"
                                        )}>
                                            <div className="p-2 rounded-lg bg-gold/10">
                                                {step.icon}
                                            </div>
                                            <h3 className={cn(
                                                "text-2xl font-semibold text-navy dark:text-white",
                                                isRTL && "font-arabic"
                                            )}>
                                                {step.title}
                                            </h3>
                                        </div>
                                        <span className={cn(
                                            "px-3 py-1 bg-gold/10 text-gold rounded-full text-sm font-medium",
                                            isRTL && "font-arabic"
                                        )}>
                                            {step.duration}
                                        </span>
                                    </div>
                                    <p className={cn(
                                        "text-slate-600 dark:text-slate-300 text-lg leading-relaxed",
                                        isRTL && "font-arabic"
                                    )}>
                                        {step.description}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Bonus Info */}
                <div className="mt-16 grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
                    <div className="bg-gold/10 rounded-2xl p-6 text-center">
                        <Plane className="w-8 h-8 text-gold mx-auto mb-3" />
                        <h4 className={cn(
                            "font-semibold text-navy dark:text-white mb-2",
                            isRTL && "font-arabic"
                        )}>{t('process.bonus_info.stay.title')}</h4>
                        <p className={cn(
                            "text-slate-600 dark:text-slate-300",
                            isRTL && "font-arabic"
                        )}>{localizeText(t('process.bonus_info.stay.duration'))}</p>
                    </div>
                    <div className="bg-gold/10 rounded-2xl p-6 text-center">
                        <Smile className="w-8 h-8 text-gold mx-auto mb-3" />
                        <h4 className={cn(
                            "font-semibold text-navy dark:text-white mb-2",
                            isRTL && "font-arabic"
                        )}>{t('process.bonus_info.social.title')}</h4>
                        <p className={cn(
                            "text-slate-600 dark:text-slate-300",
                            isRTL && "font-arabic"
                        )}>{localizeText(t('process.bonus_info.social.duration'))}</p>
                    </div>
                </div>
            </div>
        </section>
    );
} 
